import { useState, useEffect, useCallback, useRef } from 'react';
import type { ChatMessage, TypingIndicator } from '@/db/chats';
import {
  getChatMessages,
  getTypingIndicators,
  sendChatMessage,
  setTypingIndicator,
  removeTypingIndicator,
  subscribeToChatMessages,
  subscribeToTypingIndicators,
} from '@/db/chats';
import { logger } from '@/utils/logger';

interface UseChatMessagesOptions {
  chatId: string;
  userId?: string;
  userName?: string;
}

interface UseChatMessagesReturn {
  messages: Array<ChatMessage & { id: string }>;
  typingUsers: Array<TypingIndicator & { id: string }>;
  isLoading: boolean;
  error: string | null;
  sendMessage: (content: string) => Promise<boolean>;
  setTyping: (isTyping: boolean) => void;
}

// Default user info (in a real app, this would come from auth context)
const DEFAULT_USER_ID = 'current-user';
const DEFAULT_USER_NAME = 'Utente';

export function useChatMessages({
  chatId,
  userId = DEFAULT_USER_ID,
  userName = DEFAULT_USER_NAME,
}: UseChatMessagesOptions): UseChatMessagesReturn {
  const [messages, setMessages] = useState<Array<ChatMessage & { id: string }>>([]);
  const [typingUsers, setTypingUsers] = useState<Array<TypingIndicator & { id: string }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load initial messages and typing indicators
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [loadedMessages, loadedTyping] = await Promise.all([
          getChatMessages(chatId),
          getTypingIndicators(chatId),
        ]);
        setMessages(loadedMessages);
        setTypingUsers(loadedTyping.filter((t) => t.user_id !== userId));
        setError(null);
      } catch (err) {
        logger.error('Failed to load chat data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load chat data');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [chatId, userId]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!chatId) return;

    const unsubscribeMessages = subscribeToChatMessages(
      chatId,
      (newMessage) => {
        setMessages((prev) => {
          // Avoid duplicates
          if (prev.some((m) => m.id === newMessage.id)) return prev;
          return [...prev, newMessage].sort((a, b) => {
            const aTime = a.created_at_ms || 0;
            const bTime = b.created_at_ms || 0;
            return aTime - bTime;
          });
        });
      },
      (messageId) => {
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
      }
    );

    const unsubscribeTyping = subscribeToTypingIndicators(
      chatId,
      userId,
      (typing) => {
        setTypingUsers(typing.filter((t) => t.user_id !== userId));
      }
    );

    return () => {
      unsubscribeMessages();
      unsubscribeTyping();
    };
  }, [chatId, userId]);

  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isTypingRef = useRef(false);

  // Send message
  const sendMessage = useCallback(
    async (content: string): Promise<boolean> => {
      if (!content.trim()) return false;

      // Clear typing indicator when sending
      if (isTypingRef.current) {
        isTypingRef.current = false;
        removeTypingIndicator(chatId, userId);
      }

      try {
        const message = await sendChatMessage(chatId, userId, userName, content.trim());
        return !!message;
      } catch (err) {
        logger.error('Failed to send message:', err);
        return false;
      }
    },
    [chatId, userId, userName]
  );

  // Set typing indicator
  const setTyping = useCallback(
    (isTyping: boolean) => {
      if (isTyping) {
        // Set typing indicator
        if (!isTypingRef.current) {
          isTypingRef.current = true;
          setTypingIndicator(chatId, userId, userName);
        }

        // Reset the timeout
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }

        // Auto-remove typing indicator after 5 seconds of inactivity
        typingTimeoutRef.current = setTimeout(() => {
          isTypingRef.current = false;
          removeTypingIndicator(chatId, userId);
        }, 5000);
      } else {
        // Clear typing indicator
        if (isTypingRef.current) {
          isTypingRef.current = false;
          removeTypingIndicator(chatId, userId);
        }
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }
      }
    },
    [chatId, userId, userName]
  );

  // Cleanup typing indicator on unmount
  useEffect(() => {
    return () => {
      if (isTypingRef.current) {
        removeTypingIndicator(chatId, userId);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [chatId, userId]);

  return {
    messages,
    typingUsers,
    isLoading,
    error,
    sendMessage,
    setTyping,
  };
}
